import React from 'react';
import * as ReactDOM from 'react-dom';
import { LoadingIndicator, Sizes } from './loading-indicators';
import _ from 'lodash';

export enum UpdateScroll {
  AddBottom = 'Bottom',
  AddTop = 'Top'
}

interface IInfiniteScrollOwnProps {
  currentMinPage?: number;
  currentMaxPage?: number;
  loadingBottom?: boolean;
  loadingTop?: boolean;
  pageSize: number;
  totalEntities: number;

  throttleCallFrequency?: number;
  loadDistance?: number;

  loadPage: (pageNum: number, direction: UpdateScroll) => void;
}

interface IInfiniteScrollState {
  throttleFn: () => void;
  totalPages: number;
}

const loadingView = () => (
  <div style={{ position: 'relative', top: 5, height: 50 }}>
    <LoadingIndicator size={Sizes.Medium} />
  </div>
);

const notLoadingView = action => (
  <button className="btn btn-block btn-lg btn-default" onClick={action}>
    Load More Results
  </button>
);

const calcTotalPages = ({ pageSize, totalEntities }) => {
  return Math.ceil(totalEntities / pageSize);
};

export class InfiniteScroll extends React.Component<IInfiniteScrollOwnProps, IInfiniteScrollState> {
  static defaultProps = {
    currentMinPage: 1,
    currentMaxPage: 1,
    loadingBottom: false,
    loadingTop: false,

    throttleCallFrequency: 250,
    loadDistance: 250
  };

  topRef: React.RefObject<Element>;
  bottomRef: React.RefObject<Element>;

  constructor(props) {
    super(props);

    this.topRef = React.createRef();
    this.bottomRef = React.createRef();

    this.state = {
      // TODO: Remove dependancy on this lodash or explicitely import it in this file instead of in webpack.config.js.
      throttleFn: _.throttle(this.didScroll, this.props.throttleCallFrequency),
      totalPages: calcTotalPages(this.props)
    };
  }

  componentDidMount() {
    window.addEventListener('scroll', this.state.throttleFn);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.state.throttleFn);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const totalPages = calcTotalPages(nextProps);

    if (totalPages !== prevState.totalPages) {
      return { totalPages };
    }

    return null;
  }

  didScroll = () => {
    const { loadingBottom, loadingTop, loadDistance } = this.props;

    if (this.bottomRef.current && !loadingBottom) {
      const bottomNode = ReactDOM.findDOMNode(this.bottomRef.current) as Element;
      const bottomDistance = bottomNode.getBoundingClientRect().top;

      if (bottomDistance - window.innerHeight < loadDistance) {
        this.onLoadPage(UpdateScroll.AddBottom);
      }
    }

    if (this.topRef.current && !loadingTop) {
      const topNode = ReactDOM.findDOMNode(this.topRef.current) as Element;

      const topDistance = topNode.getBoundingClientRect().bottom;

      if (topDistance > -loadDistance) {
        this.onLoadPage(UpdateScroll.AddTop);
      }
    }
  }

  onLoadPage = (updateSide: UpdateScroll) => {
    const { currentMaxPage, currentMinPage, loadPage } = this.props;
    let page = currentMinPage - 1;

    if (updateSide === UpdateScroll.AddBottom) {
      page = currentMaxPage + 1;
    }
    return loadPage(page, updateSide);
  }

  onLoadPageWrapper = (updateSide: UpdateScroll) => () => this.onLoadPage(updateSide);

  renderScrollEnd = (ref, side: UpdateScroll) => (
    <div ref={ref} className={`infinite-load-more-${side.toLowerCase()}`}>
      {this.props[`loading${side}`] ? loadingView() : notLoadingView(this.onLoadPageWrapper(side))}
    </div>
  )

  render() {
    const { children, currentMinPage, currentMaxPage } = this.props;

    return (
      <React.Fragment>
        {currentMinPage > 1 ? this.renderScrollEnd(this.topRef, UpdateScroll.AddTop) : ''}

        {children}

        {currentMaxPage < this.state.totalPages ? this.renderScrollEnd(this.bottomRef, UpdateScroll.AddBottom) : ''}
      </React.Fragment>
    );
  }
}
